import { useMemo, useState } from "react";
import { getDataClient } from "@/lib/dataClient";
import type { DashboardData } from "@/hooks/useStudentDashboard";

export interface AiTaskProposal {
  title: string;
  detail: string;
  icon: string;
  subject: string;
  actionUrl: string;
  estimatedMinutes: number;
}

export interface AiLearningPlan {
  headline: string;
  explanation: string;
  reason: string;
  tasks: AiTaskProposal[];
}

function isoDate(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

function tomorrow() {
  const value = new Date();
  value.setDate(value.getDate() + 1);
  return isoDate(value);
}

function makePlan(data: DashboardData): AiLearningPlan {
  const weak = data.weakTopics[0];
  const hasLowWeek = data.weekMinutes < 120;
  const hasExam = Boolean(data.lastExam);
  const subject = weak?.subject ?? "Genel tekrar";
  const topic = weak?.topic ?? "Günün hedefi";
  const reason = weak
    ? `${subject} · ${topic} konusu %${weak.mastery_percent} düzeyinde görünüyor; önce burayı sağlamlaştırmak en yüksek getiriyi sağlar.`
    : hasLowWeek
      ? "Bu hafta çalışma süresi hedefinin altında. Kısa ve tamamlanabilir bir rota ile ritmi yeniden kuruyoruz."
      : hasExam
        ? `Son deneme sonucundan sonra bilgiyi kalıcılaştırmak için kısa tekrar ve ölçme dengesi öneriliyor.`
        : "Düzenli öğrenme döngüsü için konu, uygulama ve kısa değerlendirmeyi bir araya getiriyoruz.";

  return {
    headline: weak ? `${topic} için toparlanma rotası` : "Bugünün dengeli öğrenme rotası",
    explanation: "Plan; öğrenme verin, önceki testlerin ve günlük yük dengen üzerinden oluşturuldu. İstediğin zaman düzenleyebilir veya kapatabilirsin.",
    reason,
    tasks: [
      {
        title: `${subject}: ${topic} kısa tekrar`,
        detail: "Önce temel fikri gözden geçir, takıldığın yeri AI koça sor.",
        icon: "ri-book-open-line",
        subject,
        actionUrl: "/ogrenci/video-dersler",
        estimatedMinutes: 20,
      },
      {
        title: `${topic} · odaklı 10 soru`,
        detail: "Cevapları test bitene kadar görmeden çöz; ardından yanlışlarını incele.",
        icon: "ri-file-list-3-line",
        subject,
        actionUrl: "/ogrenci/soru-bankasi",
        estimatedMinutes: 20,
      },
      {
        title: "Odak molası ve kısa kontrol", 
        detail: "25 dakika odaklan, ardından 5 dakika ara ver. Gün sonunda hedefini kontrol et.",
        icon: "ri-timer-line",
        subject: "Odak",
        actionUrl: "/ogrenci/pomodoro",
        estimatedMinutes: 30,
      },
    ],
  };
}

export function useAiLearningOrchestrator(data: DashboardData) {
  const plan = useMemo(() => makePlan(data), [data]);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function applyTodayPlan() {
    setSaving(true);
    setStatus(null);
    try {
      const dataClient = getDataClient();
      const { data: auth } = await dataClient.auth.getUser();
      if (!auth.user) throw new Error("Planı uygulamak için giriş yapmalısın.");
      const today = isoDate();
      const { data: existing, error: existingError } = await dataClient
        .from("daily_tasks")
        .select("title")
        .eq("user_id", auth.user.id)
        .eq("task_date", today);
      if (existingError) throw existingError;
      const titles = new Set((existing ?? []).map((task: { title: string }) => task.title));
      const rows = plan.tasks
        .filter((task) => !titles.has(`AI Rotası · ${task.title}`))
        .map((task, index) => ({
          user_id: auth.user!.id,
          task_date: today,
          title: `AI Rotası · ${task.title}`,
          detail: `${task.detail} · Tahmini süre: ${task.estimatedMinutes} dk`,
          icon: task.icon,
          completed: false,
          sort_order: 100 + index,
        }));
      if (rows.length > 0) {
        const { error } = await dataClient.from("daily_tasks").insert(rows);
        if (error) throw error;
      }
      setStatus(rows.length > 0 ? "AI rotası bugünkü programa eklendi." : "Bu rota zaten bugünkü programında var.");
      return true;
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Plan eklenemedi. Lütfen tekrar dene.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function createAiHomework() {
    setSaving(true);
    setStatus(null);
    try {
      const dataClient = getDataClient();
      const { data: auth } = await dataClient.auth.getUser();
      if (!auth.user) throw new Error("Ödev oluşturmak için giriş yapmalısın.");
      const first = plan.tasks[0];
      const { error } = await dataClient.from("homework").insert({
        user_id: auth.user.id,
        title: `AI Ödevi · ${first.title}`,
        subject: first.subject,
        teacher_name: "DersDinle AI",
        due_date: tomorrow(),
        status: "bekliyor",
        description: `${first.detail} Bu ödev AI tarafından çalışma verine göre önerildi; ihtiyacına göre değiştirebilirsin.`,
      });
      if (error) throw error;
      setStatus("AI ödevi yarın için oluşturuldu.");
      return true;
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Ödev oluşturulamadı. Lütfen tekrar dene.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { plan, status, saving, applyTodayPlan, createAiHomework };
}
